/** Pull down at the top of a scroll area to sync (phones). */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { RefreshCw } from 'lucide-react';
import { syncNow } from '@/sync/status';
import { cn } from './cn';

const THRESHOLD = 70;

export function usePullToRefresh(
  ref: RefObject<HTMLElement | null>,
  onRefresh: () => Promise<void> = syncNow,
) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onStart = (e: TouchEvent) => {
      start.current = el.scrollTop <= 0 ? e.touches[0]!.clientY : null;
    };
    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const d = e.touches[0]!.clientY - start.current;
      if (d > 0 && el.scrollTop <= 0) setPull(Math.min(110, d * 0.6));
    };
    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      setPull((p) => {
        if (p >= THRESHOLD) {
          setBusy(true);
          void onRefresh().finally(() => setBusy(false));
        }
        return 0;
      });
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [ref, onRefresh]);
  return { pull, busy };
}

export function PullIndicator({ pull, busy }: { pull: number; busy: boolean }) {
  if (pull === 0 && !busy) return null;
  return (
    <div
      className="flex items-center justify-center text-ink-3"
      style={{ height: busy ? 36 : pull }}
      aria-live="polite"
    >
      <RefreshCw
        className={cn('size-4 transition-transform', busy && 'animate-spin')}
        style={{ transform: busy ? undefined : `rotate(${pull * 3}deg)` }}
      />
      <span className="sr-only">
        {busy ? 'Syncing' : pull >= THRESHOLD ? 'Release to sync' : 'Pull to sync'}
      </span>
    </div>
  );
}
